import { useState } from 'react';

interface QuoteFormProps {
  variant?: 'light' | 'dark';
  className?: string;
}

export default function QuoteForm({ variant = 'dark' }: QuoteFormProps) {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    service: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      // Simulate form submission
      await new Promise(resolve => setTimeout(resolve, 1500));
      setSubmitStatus('success');
      setFormData({ name: '', email: '', phone: '', service: '', message: '' });
      setTimeout(() => setSubmitStatus('idle'), 5000);
    } catch (error) {
      setSubmitStatus('error');
      setTimeout(() => setSubmitStatus('idle'), 5000);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    setFormData(prev => ({
      ...prev,
      [e.target.name]: e.target.value
    }));
  };

  const isDark = variant === 'dark';
  const bgClass = isDark ? 'bg-gradient-to-br from-zinc-900 to-zinc-800' : 'bg-white';
  const textClass = isDark ? 'text-white' : 'text-gray-900';
  const inputBgClass = isDark ? 'bg-zinc-800 border-zinc-700 text-white' : 'bg-gray-50 border-gray-300 text-gray-900';
  const labelClass = isDark ? 'text-zinc-300' : 'text-gray-700';

  return (
    <div className={`${bgClass} rounded-2xl p-6 sm:p-8 shadow-2xl`}>
      <h3 className={`text-2xl sm:text-3xl font-bold mb-2 ${textClass}`} style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
        Get Your Free Quote
      </h3>
      <p className={`${isDark ? 'text-zinc-400' : 'text-gray-600'} mb-6 text-sm sm:text-base`}>
        Fill out the form below and we'll get back to you within 24 hours
      </p>

      {submitStatus === 'success' && (
        <div className="mb-6 p-4 bg-green-500/20 border-2 border-green-500 rounded-lg">
          <p className="text-green-400 font-semibold flex items-center">
            <i className="ri-checkbox-circle-line text-xl mr-2"></i>
            Thank you! We'll contact you soon.
          </p>
        </div>
      )}

      {submitStatus === 'error' && (
        <div className="mb-6 p-4 bg-red-500/20 border-2 border-red-500 rounded-lg">
          <p className="text-red-400 font-semibold flex items-center">
            <i className="ri-error-warning-line text-xl mr-2"></i>
            Something went wrong. Please try again.
          </p>
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-4 sm:space-y-5">
        <div>
          <label htmlFor="name" className={`block text-sm font-semibold mb-2 ${labelClass}`}>
            Full Name *
          </label>
          <input
            type="text"
            id="name"
            name="name"
            value={formData.name}
            onChange={handleChange}
            required
            className={`w-full px-4 py-3 sm:py-4 rounded-lg border-2 ${inputBgClass} focus:border-primary-500 focus:ring-2 focus:ring-primary-500/20 outline-none transition-all text-base`}
            placeholder="John Smith"
          />
        </div>

        <div>
          <label htmlFor="email" className={`block text-sm font-semibold mb-2 ${labelClass}`}>
            Email Address *
          </label>
          <input
            type="email"
            id="email"
            name="email"
            value={formData.email}
            onChange={handleChange}
            required
            className={`w-full px-4 py-3 sm:py-4 rounded-lg border-2 ${inputBgClass} focus:border-primary-500 focus:ring-2 focus:ring-primary-500/20 outline-none transition-all text-base`}
            placeholder="john@example.com"
          />
        </div>

        <div>
          <label htmlFor="phone" className={`block text-sm font-semibold mb-2 ${labelClass}`}>
            Phone Number *
          </label>
          <input
            type="tel"
            id="phone"
            name="phone"
            value={formData.phone}
            onChange={handleChange}
            required
            className={`w-full px-4 py-3 sm:py-4 rounded-lg border-2 ${inputBgClass} focus:border-primary-500 focus:ring-2 focus:ring-primary-500/20 outline-none transition-all text-base`}
            placeholder="(402) 555-0123"
          />
        </div>

        <div>
          <label htmlFor="service" className={`block text-sm font-semibold mb-2 ${labelClass}`}>
            Service Needed *
          </label>
          <select
            id="service"
            name="service"
            value={formData.service}
            onChange={handleChange}
            required
            className={`w-full px-4 py-3 sm:py-4 rounded-lg border-2 ${inputBgClass} focus:border-primary-500 focus:ring-2 focus:ring-primary-500/20 outline-none transition-all cursor-pointer text-base`}
          >
            <option value="">Select a service</option>
            <option value="paving-estimate">Paving Estimate</option>
            <option value="asphalt-repair">Asphalt Repair</option>
            <option value="concrete-driveway">Concrete Driveway</option>
            <option value="sealcoating">Sealcoating</option>
            <option value="pothole-repair">Pothole Repair</option>
            <option value="parking-lot">Parking Lot</option>
            <option value="commercial">Commercial Services</option>
            <option value="maintenance">Maintenance Services</option>
          </select>
        </div>

        <div>
          <label htmlFor="message" className={`block text-sm font-semibold mb-2 ${labelClass}`}>
            Project Details
          </label>
          <textarea
            id="message"
            name="message"
            value={formData.message}
            onChange={handleChange}
            rows={4}
            maxLength={500}
            className={`w-full px-4 py-3 sm:py-4 rounded-lg border-2 ${inputBgClass} focus:border-primary-500 focus:ring-2 focus:ring-primary-500/20 outline-none transition-all resize-none text-base`}
            placeholder="Tell us about your project..."
          ></textarea>
          <p className={`text-xs mt-1 ${isDark ? 'text-zinc-500' : 'text-gray-500'}`}>
            {formData.message.length}/500 characters
          </p>
        </div>

        <button
          type="submit"
          disabled={isSubmitting}
          className="w-full bg-primary-600 text-white px-6 py-4 sm:py-5 rounded-lg font-bold hover:bg-primary-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap cursor-pointer shadow-lg hover:shadow-primary-600/50 text-base sm:text-lg"
        >
          {isSubmitting ? (
            <span className="flex items-center justify-center">
              <i className="ri-loader-4-line animate-spin mr-2"></i>
              Sending...
            </span>
          ) : (
            'Get Free Quote'
          )}
        </button>

        <p className={`text-xs text-center ${isDark ? 'text-zinc-500' : 'text-gray-500'}`}>
          By submitting this form, you agree to our privacy policy
        </p>
      </form>
    </div>
  );
}
